import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { geoMercator, geoPath, geoGraticule10 } from "d3-geo";
import { feature } from "topojson-client";
import land from "world-atlas/land-110m.json";
import { motion, AnimatePresence } from "framer-motion";
import {
  Satellite,
  Cpu,
  HardDrive,
  Zap,
  Thermometer,
  Radio,
  Play,
  Pause,
  RotateCcw,
  Flame,
  Waves,
  Trees,
  Factory,
  Leaf,
  Download,
  WifiOff,
  Wifi,
  Target,
  Activity,
  Signal,
  AlertTriangle,
  Clock,
  Database,
  ArrowLeft,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  reducer,
  INITIAL_STATE,
  metrics,
  formatMB,
  formatT,
  GROUND_STATIONS,
  DEMO_REGIONS,
  SEV_COLOR,
  distKm,
  FOOTPRINT_KM,
  satPosAt,
  inContact,
} from "@/lib/edge/simulator";

const MW = 1000;
const MH = 500;
const LAND = feature(land, land.objects.land);
const GRAT = geoGraticule10();

const KIND_ICONS = {
  fire: Flame,
  flood: Waves,
  "forest-change": Trees,
  agri: Leaf,
  infra: Factory,
};

export default function Edge() {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [tab, setTab] = useState("overview");

  // Tick loop
  const tickRef = useRef(null);
  useEffect(() => {
    if (!state.running) {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => dispatch({ type: "tick" }), 1000);
    return () => clearInterval(tickRef.current);
  }, [state.running]);

  const m = useMemo(() => metrics(state), [state]);
  const gs = inContact(state.pos);
  const nextContactS = useMemo(() => {
    for (let dt = 1; dt < 6000; dt += 30) {
      const p = satPosAt(state.sim_t + dt);
      if (inContact(p)) return dt;
    }
    return null;
  }, [state.sim_t]);

  const TABS = [
    { k: "overview", label: "Mission" },
    { k: "earth", label: "Earth Observation" },
    { k: "pipeline", label: "Edge AI" },
    { k: "resources", label: "Compute" },
    { k: "storage", label: "Data & Storage" },
    { k: "ground", label: "Ground Stations" },
    { k: "events", label: "Events" },
    { k: "logs", label: "Mission Logs" },
    { k: "settings", label: "Settings" },
  ];

  return (
    <div className="relative min-h-screen bg-[#02060C]" data-testid="edge-page">
      <Navbar />

      {/* Header */}
      <section className="relative overflow-hidden border-b border-white/5 pt-24 pb-6">
        <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(50%_50%_at_15%_20%,rgba(0,229,255,0.14),transparent_60%),radial-gradient(50%_45%_at_80%_30%,rgba(59,130,246,0.08),transparent_60%)]" />
        <div className="container-x relative">
          <Link to="/" className="mb-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400 hover:text-arjuna-teal">
            <ArrowLeft className="h-3 w-3" /> Home
          </Link>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-arjuna-teal">
                Arjuna Space AI India  Simulation
              </p>
              <h1 className="mt-2 font-serif text-3xl text-white sm:text-4xl lg:text-5xl">
                Arjuna <span className="text-arjuna-teal">Orbital Edge AI</span>
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-400">
                A digital twin of an Earth-observation satellite with onboard
                AI. All telemetry, orbits, detections and resources shown here
                are <span className="text-amber-400">SIMULATED</span>. No real
                hardware is connected.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <TelemetryBadge label="Mission" value={state.mission_id} />
              <TelemetryBadge label="Sat" value={state.sat_name} />
              <TelemetryBadge
                label="State"
                value={state.state.toUpperCase()}
                accent={state.state === "downlink" ? "#22D3EE" : state.state === "imaging" ? "#F59E0B" : state.state === "processing" ? "#8B5CF6" : "#22C55E"}
              />
              <TelemetryBadge label="UTC T+" value={formatT(state.sim_t)} />
            </div>
          </div>

          {/* Mission brief */}
          <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="mission-video">
            <video
              src="https://customer-assets-agu9un31.emergentagent.net/job_ocean-farm-intel/artifacts/be83ca75bcd70ee6_Arjuna%20Space%20Launch%20v2.mp4"
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full"
              poster=""
            />
          </div>

          <div className="mt-5 rounded-2xl border border-arjuna-teal/20 bg-gradient-to-br from-arjuna-teal/[0.04] to-black/40 p-5" data-testid="mission-brief">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-arjuna-teal">
              Mission Brief  AORBX-001
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-200 sm:text-[15px]">
              AORBX-1 is a sun-synchronous Earth-observation satellite with an
              onboard edge AI compute module. Instead of beaming every raw
              image back to Earth, the spacecraft <span className="text-arjuna-teal">captures,
              preprocesses, infers and prioritises</span> imagery on-orbit, then
              downlinks only the detections and lightweight evidence products
              during ground-station passes.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              This mission demonstrates the full loop: image acquisition over
              Indian landscapes (Western Ghats, Gargai Dam, Pune, Sundarbans,
              Punjab fields)  5-model AI inference (fire, flood, canopy
              change, agri stress, infrastructure change)  deterministic
              scheduling under CPU, RAM, power, thermal and bandwidth budgets
               bandwidth-aware downlink that favours critical and high-severity
              events during limited contact windows with Pune and Sriharikota
              ground stations.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-widest">
              <span className="rounded-full border border-arjuna-teal/30 bg-arjuna-teal/5 px-3 py-1 text-arjuna-teal">
                Objective  faster decisions
              </span>
              <span className="rounded-full border border-emerald-400/30 bg-emerald-400/5 px-3 py-1 text-emerald-400">
                Reduce downlink volume
              </span>
              <span className="rounded-full border border-amber-400/30 bg-amber-400/5 px-3 py-1 text-amber-400">
                Prioritise critical events
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-slate-300">
                Digital twin  simulated
              </span>
            </div>
            <p className="mt-3 text-[11px] text-slate-500">
              Press <span className="text-arjuna-teal">Start Mission</span> to begin orbit propagation and automatic observation generation. Use the trigger buttons to inject fire, flood or infrastructure events on demand and watch the pipeline, queue, map and downlink metrics update in lock-step.
            </p>
          </div>

          {/* Primary controls */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => dispatch({ type: "set_running", running: !state.running })}
              data-testid="sim-toggle"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                state.running ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-arjuna-teal text-black hover:bg-cyan-300"
              }`}
            >
              {state.running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {state.running ? "Pause" : "Start"} Mission
            </button>
            <button
              type="button"
              onClick={() => dispatch({ type: "reset" })}
              data-testid="sim-reset"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-200 hover:border-arjuna-teal/40 hover:text-arjuna-teal"
            >
              <RotateCcw className="h-4 w-4" /> Reset
            </button>
            <div className="h-6 w-px bg-white/10" />
            <TriggerBtn onClick={() => dispatch({ type: "trigger_obs", kind: "fire", region: DEMO_REGIONS[0] })} icon={Flame} color="#EF4444">
              Simulate Fire
            </TriggerBtn>
            <TriggerBtn onClick={() => dispatch({ type: "trigger_obs", kind: "flood", region: DEMO_REGIONS[2] })} icon={Waves} color="#22D3EE">
              Simulate Flood
            </TriggerBtn>
            <TriggerBtn onClick={() => dispatch({ type: "trigger_obs", kind: "agri", region: DEMO_REGIONS[4] })} icon={Leaf} color="#22C55E">
              Agri Anomaly
            </TriggerBtn>
            <TriggerBtn onClick={() => dispatch({ type: "trigger_obs" })} icon={Target} color="#8B5CF6">
              Generate Obs
            </TriggerBtn>
            <div className="h-6 w-px bg-white/10" />
            <TriggerBtn
              onClick={() => dispatch({ type: "contact_override", v: state.contact_override === "force-off" ? null : "force-off" })}
              icon={WifiOff}
              color="#F43F5E"
              active={state.contact_override === "force-off"}
            >
              {state.contact_override === "force-off" ? "Restore Contact" : "Lose Contact"}
            </TriggerBtn>
            <TriggerBtn
              onClick={() => dispatch({ type: "contact_override", v: state.contact_override === "force-on" ? null : "force-on" })}
              icon={Wifi}
              color="#22C55E"
              active={state.contact_override === "force-on"}
            >
              Force Downlink
            </TriggerBtn>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex flex-wrap gap-1.5 border-b border-white/5 pb-0.5 text-xs">
            {TABS.map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`relative rounded-t-md px-3 py-2 font-mono uppercase tracking-widest transition-colors ${
                  tab === t.k ? "bg-white/[0.04] text-arjuna-teal" : "text-slate-400 hover:text-white"
                }`}
              >
                {t.label}
                {tab === t.k && <span className="absolute inset-x-2 -bottom-px h-px bg-arjuna-teal" />}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-8">
        <div className="container-x">
          {tab === "overview" && <MissionOverview state={state} m={m} nextContactS={nextContactS} gs={gs} />}
          {tab === "earth" && <EarthObservation state={state} dispatch={dispatch} />}
          {tab === "pipeline" && <EdgePipeline state={state} />}
          {tab === "resources" && <ComputeResources state={state} dispatch={dispatch} />}
          {tab === "storage" && <StorageQueue state={state} />}
          {tab === "ground" && <GroundStationsPanel state={state} gs={gs} nextContactS={nextContactS} m={m} />}
          {tab === "events" && <EventIntelligence state={state} />}
          {tab === "logs" && <MissionLogsPanel state={state} />}
          {tab === "settings" && <SettingsPanel state={state} dispatch={dispatch} />}
        </div>
      </section>
    </div>
  );
}

// =========================
// Shared presentational bits
// =========================

function TelemetryBadge({ label, value, accent = "#22D3EE" }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
      <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-slate-500">{label}</p>
      <p className="mt-0.5 font-mono text-sm font-semibold" style={{ color: accent }}>
        {value}
      </p>
    </div>
  );
}

function TriggerBtn({ children, onClick, icon: Icon, color = "#22D3EE", active = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
        active ? "border-transparent bg-opacity-20 text-black" : "border-white/10 bg-white/[0.03] text-slate-200 hover:bg-white/[0.06]"
      }`}
      style={active ? { background: color, color: "#021117" } : {}}
    >
      <Icon className="h-3.5 w-3.5" style={active ? {} : { color }} />
      {children}
    </button>
  );
}

function Card({ title, kicker, children, right, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          {kicker && <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-arjuna-teal">{kicker}</p>}
          <h3 className="mt-0.5 font-serif text-lg text-white">{title}</h3>
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function KV({ k, v, accent }) {
  return (
    <div className="flex items-baseline justify-between border-b border-white/5 py-1.5 last:border-b-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{k}</span>
      <span className="font-mono text-sm text-white" style={accent ? { color: accent } : {}}>
        {v}
      </span>
    </div>
  );
}

function Bar({ value, max, color = "#22D3EE", label, danger }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const over = danger && pct > danger;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
        <span className="font-mono text-xs text-white">
          {typeof value === "number" ? value.toFixed(value < 10 ? 1 : 0) : value} / {max}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.05]">
        <motion.div
          className="h-full rounded-full"
          style={{ background: over ? "#F43F5E" : color, boxShadow: `0 0 10px ${over ? "#F43F5E" : color}66` }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>
    </div>
  );
}

// =========================
// MISSION OVERVIEW
// =========================

function MissionOverview({ state, m, nextContactS, gs }) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Card kicker="Telemetry" title="Spacecraft Status" testid="card-telemetry">
          <div className="grid grid-cols-2 gap-x-6">
            <KV k="Altitude" v="500.0 km" />
            <KV k="Velocity" v="7.612 km/s" />
            <KV k="Latitude" v={`${state.pos.lat.toFixed(3)}°`} />
            <KV k="Longitude" v={`${state.pos.lon.toFixed(3)}°`} />
            <KV k="Inclination" v="65.0°" />
            <KV k="RAAN" v="20.0°" />
            <KV k="Mode" v={state.state.toUpperCase()} accent="#22D3EE" />
            <KV k="Orbit #" v={Math.floor(state.sim_t / 5700) + 1} />
          </div>
        </Card>
      </div>

      <div className="lg:col-span-4">
        <Card kicker="Onboard Resources" title="Edge Computer" testid="card-resources">
          <div className="space-y-3">
            <Bar label="CPU %" value={state.resources.cpu_pct} max={100} color="#22D3EE" danger={75} />
            <Bar label="RAM MB" value={state.resources.ram_mb} max={state.resources.ram_limit} color="#8B5CF6" />
            <Bar label="Storage MB" value={state.resources.storage_mb} max={state.resources.storage_limit} color="#F59E0B" />
            <Bar label="Power W" value={state.resources.power_w} max={state.resources.power_limit} color="#22C55E" danger={80} />
            <Bar label="Temp °C" value={state.resources.temp_c} max={state.resources.temp_limit} color="#EF4444" danger={80} />
          </div>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card kicker="Mission KPIs" title="This Orbit" testid="card-kpis">
          <KV k="Observations" v={state.observations.length} />
          <KV k="Processed" v={state.observations.filter((o) => o.stage === "queue").length} />
          <KV k="Queue" v={state.queue.length} />
          <KV k="Detections" v={m.detections} accent="#F59E0B" />
          <KV k="Downlinked" v={state.downlinked.length} accent="#22D3EE" />
          <KV
            k="Next Contact"
            v={gs ? `IN CONTACT (${gs.name})` : nextContactS ? `T+${Math.floor(nextContactS / 60)}m` : "–"}
            accent={gs ? "#22C55E" : "#94A3B8"}
          />
        </Card>
      </div>

      {/* Mini map */}
      <div className="lg:col-span-12">
        <Card kicker="Live Sub-track" title="Ground Track & Footprint" testid="card-minimap">
          <WorldMap state={state} compact />
        </Card>
      </div>

      {/* Downlink comparison */}
      <div className="lg:col-span-12">
        <Card kicker="Intelligent Downlink" title="Edge AI vs Store-and-Forward" testid="card-compare">
          <DownlinkCompare m={m} state={state} />
        </Card>
      </div>
    </div>
  );
}

// =========================
// EARTH OBSERVATION (map + regions)
// =========================

function EarthObservation({ state, dispatch }) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <div className="lg:col-span-9">
        <Card title="Interactive Earth" kicker="Mission Map" testid="card-earth-map">
          <WorldMap state={state} />
          <p className="mt-3 text-[11px] text-slate-500">
            Satellite ground track, footprint ({FOOTPRINT_KM} km), ground stations (circles) and detected events (coloured markers). Values are simulated for demonstration.
          </p>
        </Card>
      </div>

      <div className="lg:col-span-3 space-y-4">
        <Card title="Target Regions" kicker="Observation" testid="card-regions">
          <ul className="space-y-2">
            {DEMO_REGIONS.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => dispatch({ type: "trigger_obs", region: r })}
                  data-testid={`region-${r.id}`}
                  className="group flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-left transition-colors hover:border-arjuna-teal/40 hover:bg-arjuna-teal/5"
                >
                  <div>
                    <p className="text-sm font-semibold text-white">{r.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {r.lat.toFixed(2)}°, {r.lon.toFixed(2)}°  {r.type}
                    </p>
                  </div>
                  <Target className="h-4 w-4 text-slate-500 group-hover:text-arjuna-teal" />
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Current Target" kicker="Footprint" testid="card-current-target">
          {(() => {
            const nearest = [...DEMO_REGIONS].sort(
              (a, b) => distKm(state.pos, a) - distKm(state.pos, b)
            )[0];
            const d = distKm(state.pos, nearest);
            return (
              <>
                <p className="font-serif text-lg text-white">{nearest.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Distance from nadir
                </p>
                <p className="mt-1 font-mono text-lg text-arjuna-teal">{d.toFixed(0)} km</p>
                <p className="mt-3 text-[11px] text-slate-500">
                  Imaging opportunity when within {FOOTPRINT_KM} km of nadir.
                </p>
              </>
            );
          })()}
        </Card>
      </div>
    </div>
  );
}

// =========================
// EDGE AI PIPELINE
// =========================

function EdgePipeline({ state }) {
  const stages = ["preprocess", "quality", "inference", "classify", "prioritize", "compress", "queue"];
  const stageLabels = ["Preproc", "QA", "AI Infer", "Classify", "Prioritise", "Compress", "Queue"];
  const counts = stages.map((s) => state.observations.filter((o) => o.stage === s).length);

  return (
    <div className="space-y-5">
      <Card kicker="Edge AI" title="Processing Pipeline" testid="card-pipeline">
        <div className="flex flex-wrap items-center gap-2">
          {stages.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-center">
                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{stageLabels[i]}</p>
                <p className="mt-0.5 font-mono text-sm text-arjuna-teal">{counts[i]}</p>
              </div>
              {i < stages.length - 1 && (
                <span className="h-px w-6 bg-gradient-to-r from-arjuna-teal/70 to-arjuna-teal/10" />
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card kicker="Latest Observations" title="Observation Log" testid="card-obs-log">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left font-mono uppercase tracking-widest text-slate-500">
                <th className="py-2">ID</th>
                <th>Region</th>
                <th>Sensor</th>
                <th>Model</th>
                <th>Raw</th>
                <th>Inf (ms)</th>
                <th>Class</th>
                <th>Conf</th>
                <th>Severity</th>
                <th>Out</th>
                <th>Stage</th>
                <th>Decision</th>
              </tr>
            </thead>
            <tbody className="font-mono text-slate-200">
              <AnimatePresence>
                {state.observations.slice(0, 25).map((o) => {
                  const Icon = KIND_ICONS[o.kind] || Activity;
                  return (
                    <motion.tr
                      key={o.id}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-t border-white/5"
                    >
                      <td className="py-1.5 text-arjuna-teal">{o.id}</td>
                      <td>{o.region}</td>
                      <td className="text-slate-400">{o.sensor}</td>
                      <td className="text-slate-400">{o.model}</td>
                      <td>{o.raw_mb} MB</td>
                      <td>{o.inference_ms}</td>
                      <td className="flex items-center gap-1 py-1.5">
                        <Icon className="h-3 w-3 text-arjuna-teal" />
                        {o.class}
                      </td>
                      <td>{(o.confidence * 100).toFixed(0)}%</td>
                      <td style={{ color: SEV_COLOR[o.severity] }}>{o.severity}</td>
                      <td>{o.out_kb} kb</td>
                      <td className="text-arjuna-teal">{o.stage}</td>
                      <td className={o.retained ? "text-emerald-400" : "text-slate-500"}>
                        {o.retained ? "RETAIN+TX" : "DISCARD"}
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
          {state.observations.length === 0 && (
            <p className="py-8 text-center text-xs text-slate-500">
              No observations yet. Start the mission or trigger a simulated event.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}

// =========================
// COMPUTE RESOURCES
// =========================

function ComputeResources({ state, dispatch }) {
  const r = state.resources;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Card kicker="Resource Manager" title="Onboard Budget" testid="card-budget">
          <div className="space-y-3">
            <Bar label="CPU %" value={r.cpu_pct} max={100} color="#22D3EE" danger={75} />
            <Bar label="RAM MB" value={r.ram_mb} max={r.ram_limit} color="#8B5CF6" />
            <Bar label="Storage MB" value={r.storage_mb} max={r.storage_limit} color="#F59E0B" />
            <Bar label="Power W" value={r.power_w} max={r.power_limit} color="#22C55E" danger={80} />
            <Bar label="Temp °C" value={r.temp_c} max={r.temp_limit} color="#EF4444" danger={80} />
            <Bar label="Bandwidth Mbps" value={r.bandwidth_mbps} max={100} color="#22D3EE" />
          </div>
        </Card>
      </div>

      <div className="lg:col-span-7">
        <Card kicker="Scheduler Policy" title="Deterministic Prioritisation" testid="card-scheduler">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Policy</label>
              <select
                value={state.policy}
                onChange={(e) => dispatch({ type: "set_policy", v: e.target.value })}
                data-testid="policy-select"
                className="rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-xs text-white outline-none focus:border-arjuna-teal"
              >
                <option value="severity-first">severity-first</option>
                <option value="fifo">fifo</option>
                <option value="age">age</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                CPU limit ({r.cpu_limit}%)
              </label>
              <input
                type="range"
                min={30}
                max={100}
                step={5}
                value={r.cpu_limit}
                onChange={(e) => dispatch({ type: "set_cpu_limit", v: parseInt(e.target.value, 10) })}
                className="w-full accent-arjuna-teal"
                data-testid="cpu-limit"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                Downlink bandwidth ({state.bandwidth} Mbps)
              </label>
              <input
                type="range"
                min={1}
                max={100}
                step={1}
                value={state.bandwidth}
                onChange={(e) => dispatch({ type: "set_bandwidth", v: parseInt(e.target.value, 10) })}
                className="w-full accent-arjuna-teal"
                data-testid="bandwidth"
              />
            </div>
            <div className="rounded-xl border border-white/10 bg-black/40 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Decision rule</p>
              <p className="mt-1 font-mono text-xs text-slate-200">
                queue = sort(obs, [{state.policy === "severity-first" ? "severity DESC, created ASC" : state.policy === "fifo" ? "created ASC" : "age DESC"}])
                <br />
                while bandwidth: tx(queue.shift())
                <br />
                defer_if_over(cpu, ram, power, temp)
              </p>
              <p className="mt-2 text-[11px] text-slate-500">
                Deterministic & inspectable so demonstrations are reproducible.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// =========================
// STORAGE / QUEUE
// =========================

function StorageQueue({ state }) {
  const [sortKey, setSortKey] = useState("priority");
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => {
    let r = [...state.queue];
    if (filter !== "all") r = r.filter((x) => x.severity === filter);
    r.sort((a, b) => {
      if (sortKey === "priority") return b.priority - a.priority;
      if (sortKey === "size") return b.size_kb - a.size_kb;
      return a.created_t - b.created_t;
    });
    return r;
  }, [state.queue, sortKey, filter]);

  return (
    <div className="space-y-5">
      <Card kicker="Data & Storage" title="Onboard Task Queue" testid="card-queue"
        right={
          <div className="flex gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border border-white/10 bg-black/40 px-2 py-1 text-[11px] text-white outline-none" data-testid="queue-filter">
              <option value="all">all</option>
              <option value="critical">critical</option>
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
            </select>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="rounded-full border border-white/10 bg-black/40 px-2 py-1 text-[11px] text-white outline-none" data-testid="queue-sort">
              <option value="priority">sort: priority</option>
              <option value="size">sort: size</option>
              <option value="age">sort: age</option>
            </select>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left font-mono uppercase tracking-widest text-slate-500">
                <th className="py-2">Task</th>
                <th>Type</th>
                <th>Size</th>
                <th>Priority</th>
                <th>Created</th>
                <th>State</th>
                <th>Retries</th>
                <th>Retain</th>
              </tr>
            </thead>
            <tbody className="font-mono text-slate-200">
              {rows.map((q) => (
                <tr key={q.id} className="border-t border-white/5">
                  <td className="py-1.5 text-arjuna-teal">{q.id}</td>
                  <td>{q.type}</td>
                  <td>{formatKB(q.size_kb)}</td>
                  <td style={{ color: SEV_COLOR[q.severity] }}>{q.severity}</td>
                  <td>T+{formatT(q.created_t)}</td>
                  <td>{q.state}</td>
                  <td>{q.retries}</td>
                  <td>{q.retain_until_s}s</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <p className="py-8 text-center text-xs text-slate-500">Queue empty.</p>
          )}
        </div>
      </Card>

      <Card kicker="Delivery" title="Downlinked" testid="card-downlinked">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left font-mono uppercase tracking-widest text-slate-500">
                <th className="py-2">Task</th>
                <th>Type</th>
                <th>Size</th>
                <th>Priority</th>
                <th>Downlink T+</th>
                <th>Ground Station</th>
              </tr>
            </thead>
            <tbody className="font-mono text-slate-200">
              {state.downlinked.slice(0, 25).map((d) => (
                <tr key={d.id + d.downlinked_t} className="border-t border-white/5">
                  <td className="py-1.5 text-arjuna-teal">{d.id}</td>
                  <td>{d.type}</td>
                  <td>{formatKB(d.size_kb)}</td>
                  <td style={{ color: SEV_COLOR[d.severity] }}>{d.severity}</td>
                  <td>{formatT(d.downlinked_t)}</td>
                  <td>{d.ground_station}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {state.downlinked.length === 0 && (
            <p className="py-8 text-center text-xs text-slate-500">No deliveries yet.</p>
          )}
        </div>
      </Card>
    </div>
  );
}

// =========================
// GROUND STATIONS
// =========================

function GroundStationsPanel({ state, gs, nextContactS, m }) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Card kicker="Ground Stations" title="Contact Status" testid="card-ground">
          <ul className="space-y-3">
            {GROUND_STATIONS.map((g) => {
              const d = distKm(state.pos, g);
              const active = d < g.radius_km;
              return (
                <li key={g.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Radio className={`h-4 w-4 ${active ? "text-emerald-400" : "text-slate-500"}`} />
                      <p className="font-semibold text-white">{g.name}</p>
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${active ? "border-emerald-400/40 text-emerald-400" : "border-white/10 text-slate-400"}`}>
                      {active ? "IN CONTACT" : "idle"}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-slate-300">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Range</p>
                      <p className="mt-0.5">{d.toFixed(0)} km</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Capacity</p>
                      <p className="mt-0.5">{state.bandwidth} Mbps</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Awaiting</p>
                      <p className="mt-0.5">{state.queue.length} task(s)</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <div className="lg:col-span-5">
        <Card kicker="Session" title="Next Contact Window" testid="card-next-contact">
          <div className="space-y-3">
            <KV k="Current" v={gs ? gs.name : "none"} accent={gs ? "#22C55E" : "#94A3B8"} />
            <KV k="Next (sim)" v={gs ? "— in contact —" : nextContactS ? `T+${Math.floor(nextContactS / 60)}m ${nextContactS % 60}s` : "—"} />
            <KV k="High-pri sent" v={m.high_pri_sent} accent="#22D3EE" />
            <KV k="Low-pri deferred" v={m.low_pri_deferred} />
            <KV k="Edge TX (MB)" v={m.edge_total_mb.toFixed(2)} accent="#22D3EE" />
            <KV k="Conv projection (MB)" v={m.conv_total_mb.toFixed(2)} accent="#F59E0B" />
          </div>
        </Card>
      </div>
    </div>
  );
}

// =========================
// EVENT INTELLIGENCE
// =========================

function EventIntelligence({ state }) {
  const [sev, setSev] = useState("all");
  const rows = state.events.filter((e) => sev === "all" || e.severity === sev);

  const exportCsv = () => {
    const rows2 = [["id","type","lat","lon","region","confidence","severity","source_obs","status","downlink","ts"], ...state.events.map((e) => [e.id, e.type, e.lat.toFixed(5), e.lon.toFixed(5), e.region, e.confidence, e.severity, e.source_obs, e.status, e.downlink, e.ts])];
    const csv = rows2.map((r) => r.map(cell).join(",")).join("\n");
    download("arjuna-events.csv", csv, "text/csv");
  };
  const exportGeoJson = () => {
    const fc = {
      type: "FeatureCollection",
      features: state.events.map((e) => ({
        type: "Feature",
        properties: { id: e.id, type: e.type, severity: e.severity, confidence: e.confidence, region: e.region, source_obs: e.source_obs, status: e.status, ts: e.ts },
        geometry: { type: "Point", coordinates: [e.lon, e.lat] },
      })),
    };
    download("arjuna-events.geojson", JSON.stringify(fc, null, 2), "application/geo+json");
  };

  return (
    <Card
      kicker="Events"
      title={`Detections (${state.events.length})`}
      testid="card-events"
      right={
        <div className="flex gap-2">
          <select value={sev} onChange={(e) => setSev(e.target.value)} className="rounded-full border border-white/10 bg-black/40 px-2 py-1 text-[11px] text-white outline-none" data-testid="event-sev">
            <option value="all">all severities</option>
            <option value="critical">critical</option>
            <option value="high">high</option>
            <option value="medium">medium</option>
            <option value="low">low</option>
          </select>
          <button type="button" onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-slate-200 hover:border-arjuna-teal/40">
            <Download className="h-3 w-3" /> CSV
          </button>
          <button type="button" onClick={exportGeoJson} data-testid="export-geojson-events" className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-slate-200 hover:border-arjuna-teal/40">
            <Download className="h-3 w-3" /> GeoJSON
          </button>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left font-mono uppercase tracking-widest text-slate-500">
              <th className="py-2">Event</th>
              <th>Type</th>
              <th>Region</th>
              <th>Coords</th>
              <th>Conf</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Downlink</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody className="font-mono text-slate-200">
            {rows.slice(0, 60).map((e) => {
              const Icon = KIND_ICONS[e.kind] || AlertTriangle;
              return (
                <tr key={e.id} className="border-t border-white/5">
                  <td className="py-1.5 text-arjuna-teal">{e.id}</td>
                  <td className="flex items-center gap-1 py-1.5">
                    <Icon className="h-3 w-3" style={{ color: SEV_COLOR[e.severity] }} />
                    {e.type}
                  </td>
                  <td>{e.region}</td>
                  <td>
                    {e.lat.toFixed(2)}°, {e.lon.toFixed(2)}°
                  </td>
                  <td>{(e.confidence * 100).toFixed(0)}%</td>
                  <td style={{ color: SEV_COLOR[e.severity] }}>{e.severity}</td>
                  <td>{e.status}</td>
                  <td>{e.downlink}</td>
                  <td className="text-slate-400">{e.action}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <p className="py-8 text-center text-xs text-slate-500">No events detected yet.</p>}
      </div>
    </Card>
  );
}

// =========================
// MISSION LOGS
// =========================

function MissionLogsPanel({ state }) {
  return (
    <Card kicker="Audit" title="Mission Log" testid="card-logs">
      <div className="max-h-[520px] overflow-y-auto rounded-xl border border-white/5 bg-black/40 p-3 font-mono text-[11px]">
        {state.log.length === 0 && <p className="text-slate-500">No events yet.</p>}
        {state.log.map((l, i) => (
          <div key={i} className="flex items-start gap-3 border-b border-white/5 py-1 last:border-b-0">
            <span className="shrink-0 text-slate-500">T+{formatT(l.t)}</span>
            <span className="text-slate-200">{l.msg}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// =========================
// SETTINGS
// =========================

function SettingsPanel({ state, dispatch }) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <Card kicker="Simulation" title="Clock & Reproducibility" testid="card-settings-clock">
        <KV k="Seed" v={state.seed} />
        <KV k="Simulated time" v={formatT(state.sim_t)} />
        <KV k="Running" v={state.running ? "yes" : "no"} accent={state.running ? "#22C55E" : "#F59E0B"} />
        <KV k="Tick rate" v="1 Hz" />
        <KV k="Mode" v="prototype / local" />
        <p className="mt-3 text-[11px] text-slate-500">
          State is held in your browser. Nothing is persisted to a backend yet. Reset to re-seed.
        </p>
      </Card>
      <Card kicker="Boundaries" title="Not Flight Software" testid="card-settings-boundaries">
        <p className="text-sm text-slate-300">
          This application is a <span className="text-amber-400">digital twin</span> for
          demonstrations. Orbit propagation uses a simplified two-body model;
          revisit times and footprint are approximate. No commands are sent to
          any spacecraft or ground station.
        </p>
        <ul className="mt-3 space-y-1 text-xs text-slate-400">
          <li> Credentials never hardcoded; use environment variables.</li>
          <li> AI inference is synthetic unless a real model is configured.</li>
          <li> Scheduler decisions are deterministic for reproducibility.</li>
        </ul>
      </Card>
    </div>
  );
}

// =========================
// WORLD MAP (shared)
// =========================

function WorldMap({ state, compact = false }) {
  const projection = useMemo(
    () =>
      geoMercator()
        .scale(((MW / (2 * Math.PI)) * 0.95))
        .translate([MW / 2, MH / 2 + 50]),
    []
  );
  const path = geoPath(projection);
  const landPath = path(LAND);
  const grat = path(GRAT);
  const satXY = projection([state.pos.lon, state.pos.lat]);

  // Approximate footprint radius in pixels at equator
  const footprintPx = projection([0, 0])[0] === projection([5, 0])[0] ? 20 : Math.abs(projection([FOOTPRINT_KM / 111, state.pos.lat])[0] - projection([0, state.pos.lat])[0]);

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/5 bg-[#02070E] ${compact ? "" : "aspect-[2/1]"}`}>
      <svg viewBox={`0 0 ${MW} ${MH}`} className="w-full">
        <path d={grat} fill="none" stroke="rgba(0,229,255,0.08)" strokeWidth="0.6" />
        <path d={landPath} fill="rgba(0,229,255,0.07)" stroke="rgba(0,229,255,0.4)" strokeWidth="0.8" />

        {/* Ground stations */}
        {GROUND_STATIONS.map((g) => {
          const p = projection([g.lon, g.lat]);
          const r = Math.max(14, Math.abs(projection([g.lon + g.radius_km / 111, g.lat])[0] - p[0]));
          return (
            <g key={g.id}>
              <circle cx={p[0]} cy={p[1]} r={r} fill="rgba(34,197,94,0.08)" stroke="rgba(34,197,94,0.4)" strokeDasharray="4 4" />
              <circle cx={p[0]} cy={p[1]} r="4" fill="#22C55E" />
              <text x={p[0] + 8} y={p[1] + 4} fontSize="12" fill="#22C55E" fontFamily="ui-monospace, monospace">
                {g.name}
              </text>
            </g>
          );
        })}

        {/* Target regions */}
        {DEMO_REGIONS.map((r) => {
          const p = projection([r.lon, r.lat]);
          return (
            <g key={r.id}>
              <rect x={p[0] - 6} y={p[1] - 6} width="12" height="12" fill="none" stroke="rgba(0,229,255,0.6)" strokeDasharray="2 2" />
              <text x={p[0] + 10} y={p[1] + 4} fontSize="11" fill="#94A3B8" fontFamily="ui-monospace, monospace">
                {r.name}
              </text>
            </g>
          );
        })}

        {/* Events */}
        {state.events.slice(0, 40).map((e) => {
          const p = projection([e.lon, e.lat]);
          return (
            <circle key={e.id} cx={p[0]} cy={p[1]} r="5" fill={SEV_COLOR[e.severity]} stroke="#02070E" strokeWidth="1.5">
              <animate attributeName="r" values="3;8;3" dur="2.5s" repeatCount="indefinite" />
            </circle>
          );
        })}

        {/* Satellite footprint */}
        <circle cx={satXY[0]} cy={satXY[1]} r={Math.max(20, footprintPx)} fill="rgba(0,229,255,0.08)" stroke="rgba(0,229,255,0.5)" strokeDasharray="3 3" />

        {/* Satellite */}
        <g>
          <circle cx={satXY[0]} cy={satXY[1]} r="12" fill="none" stroke="#00E5FF" opacity="0.5">
            <animate attributeName="r" values="10;22;10" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.8;0;0.8" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <polygon
            points={`${satXY[0]},${satXY[1] - 8} ${satXY[0] + 8},${satXY[1] + 6} ${satXY[0] - 8},${satXY[1] + 6}`}
            fill="#00E5FF"
          />
        </g>
      </svg>
      <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-2 rounded-full border border-arjuna-teal/40 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-arjuna-teal backdrop-blur">
        <Satellite className="h-3 w-3" /> {state.sat_name}  SIMULATED
      </div>
      <div className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 backdrop-blur">
        <Signal className="h-3 w-3" /> {state.pos.lat.toFixed(2)}°, {state.pos.lon.toFixed(2)}°
      </div>
    </div>
  );
}

// =========================
// DOWNLINK COMPARE
// =========================

function DownlinkCompare({ m, state }) {
  const reduction = Math.max(0, m.reduction_pct);
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Workflow 1  Store & Forward</p>
        <p className="mt-2 font-serif text-2xl text-white">{formatMB(m.conv_total_mb)}</p>
        <p className="text-xs text-slate-400">Raw observations awaiting / sent</p>
        <div className="mt-3 space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">Queue</span><span>{state.queue.length}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Time to detect</span><span className="text-amber-400">= contact</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Time to deliver</span><span className="text-amber-400">= next pass</span></div>
        </div>
      </div>

      <div className="rounded-2xl border border-arjuna-teal/40 bg-arjuna-teal/[0.05] p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-arjuna-teal">Workflow 2  Edge AI</p>
        <p className="mt-2 font-serif text-2xl text-white">{formatMB(m.edge_total_mb)}</p>
        <p className="text-xs text-slate-400">Processed outputs transmitted</p>
        <div className="mt-3 space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">Downlinked tasks</span><span className="text-arjuna-teal">{state.downlinked.length}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Time to detect</span><span className="text-arjuna-teal">onboard, immediate</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Time to deliver</span><span className="text-arjuna-teal">next pass (lightweight)</span></div>
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-400/40 bg-emerald-400/[0.05] p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Impact</p>
        <p className="mt-2 font-serif text-3xl text-white">{reduction.toFixed(1)}%</p>
        <p className="text-xs text-slate-400">Est. downlink reduction (simulated)</p>
        <div className="mt-3 space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">High-pri sent</span><span className="text-emerald-400">{m.high_pri_sent}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Low-pri deferred</span><span>{m.low_pri_deferred}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Detections</span><span className="text-arjuna-teal">{m.detections}</span></div>
        </div>
      </div>
    </div>
  );
}

// =========================
// Helpers
// =========================

function formatKB(kb) {
  if (!isFinite(kb)) return "0 kb";
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${kb.toFixed(0)} kb`;
}

function cell(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function download(filename, body, mime) {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
